/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { FormInstance } from 'antd/lib/form';
import { message } from '@revfluence/fresh';
import { OFFER_PAYOUT_TYPE, OFFER_SOURCE, OFFER_CONVERSION_TYPE, PayoutRuleOperator, PayoutRuleInput, PRODUCT_CONDITION_TYPE, OFFER_TRACKING_TYPE } from '@affiliates/types/globalTypes';
import { PlusIcon, XmarkIcon, CheckIcon, LockIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card } from '@frontend/shadcn/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@frontend/shadcn/components/ui/select';
import { EditOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import Tag from '@frontend/app/refresh-components/Tag';
import { EllipsisIcon, TriangleExclamationIcon } from '@revfluence/fresh-icons/solid/esm';
import { Modal } from '@revfluence/fresh';
import { sortBy } from 'lodash';
import { Input } from '@frontend/shadcn/components/ui/input';
import { UseNaturalNumbers } from '@frontend/applications/AffiliatesApp/common-utils/useNaturalNumbers';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { P } from '@frontend/shadcn/components/typography/p';
import PayoutTags from '@frontend/applications/AffiliatesApp/components/PayoutRules/components/PayoutTags';
import PayoutRulesDrawer, { getPayoutText } from '@frontend/applications/AffiliatesApp/components/PayoutRules/PayoutRulesDrawer';
import moment from 'moment';
import { ConditionType, OPERATOR_DISPLAY, PayoutAction, PayoutRuleConditionsInput } from '@frontend/applications/AffiliatesApp/components/PayoutRules/components/types';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import {
  ClientForSync,
  FormAsyncAction, IAffiliateLinksFormValues, ICommonFormValues, IPayoutVariant, OfferFormAsyncActions, OfferFormModes,
} from '../../../..';
import { getPayoutTag, getPayoutTagInitials, getToolTipContent, getUniqueColorForPayout } from '../../../../utils/OfferFormUtils';

const { confirm } = Modal;

interface IEvents {
  onBlur: () => void;
  onFocus: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

enum PAYOUT_MODE {
  CREATE = 'create',
  EDIT = 'edit'
}

interface IProps {
  events: {
    onBlur: IEvents['onBlur'];
    options: {
      [OFFER_PAYOUT_TYPE.CONVERSION]: IEvents;
      [OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE]: IEvents;
      [OFFER_PAYOUT_TYPE.SALE]: IEvents;
      [OFFER_PAYOUT_TYPE.CLICK]: IEvents;
    };
  };
  formattedConversionType: string;
  showCostPerClick: boolean;
  value: ICommonFormValues['payoutType'] | ICommonFormValues['payoutOptions'];
  source: OFFER_SOURCE;
  mode: OfferFormModes;
  formRef: FormInstance;
  handleFormAsyncActions?: (value: FormAsyncAction) => void;
  disabled: boolean;
  conversionType?: string;
  affiliateLinkData?: IAffiliateLinksFormValues;
  shopifyData?: {
    clientsForSync: ClientForSync[];
  };
}

export const RefreshOfferPayoutOptions: React.FC<Readonly<IProps>> = React.memo(({
  showCostPerClick,
  value,
  source,
  mode,
  formRef,
  handleFormAsyncActions,
  formattedConversionType,
  conversionType,
  shopifyData,
  affiliateLinkData,
}) => {
  const { tieredCommissions } = useClientFeatures();
  const MAX_PAYOUT_OPTIONS = 10;
  const payouts: IPayoutVariant[] = value as IPayoutVariant[];
  const [tempPayoutTitles, setTempPayoutTitles] = React.useState('');
  const [tempPercentPayout, setTempPercentPayout] = React.useState<number | null>(null);
  const [tempFlatPayout, setTempFlatPayout] = React.useState<number | null>(null);
  const [tempPayoutType, setTempPayoutType] = React.useState(OFFER_PAYOUT_TYPE.CONVERSION);
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = React.useState(false);
  const [payoutMode, setPayoutMode] = React.useState<PAYOUT_MODE>(PAYOUT_MODE.CREATE);
  const [disableAddPayout, setDisableAddPayout] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [isPayoutRulesDrawerOpen, setIsPayoutRulesDrawerOpen] = React.useState(false);
  const [currentPayout, setCurrentPayout] = React.useState<IPayoutVariant | null>(null);
  const [currentPayoutIndex, setCurrentPayoutIndex] = React.useState<number | null>(null);
  const [payoutRules, setPayoutRules] = React.useState<PayoutRuleInput[]>([]);
  const [conditions, setConditions] = React.useState<PayoutRuleConditionsInput[]>([]);
  const [expandedPayouts, setExpandedPayouts] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    const validationErrors = getToolTipContent(tempPayoutTitles, tempFlatPayout, tempPercentPayout, payouts, setDisableAddPayout, tempPayoutType, payoutMode);
    setErrors(validationErrors);
  }, [tempPayoutTitles, tempFlatPayout, tempPercentPayout, payouts, payoutMode, tempPayoutType]);
  const [payoutState, setPayoutState] = React.useState({
    tempPayoutTitles: '',
    tempPercentPayout: null,
    tempFlatPayout: null,
    tempPayoutType: OFFER_PAYOUT_TYPE.CONVERSION,
  });

  const updatePayoutState = (key, value) => {
    setPayoutState((prev) => ({ ...prev, [key]: value }));
    switch (key) {
      case 'tempPayoutTitles':
        setTempPayoutTitles(value);
        break;
      case 'tempPercentPayout':
        setTempPercentPayout(value);
        break;
      case 'tempFlatPayout':
        setTempFlatPayout(value);
        break;
      case 'tempPayoutType':
        setTempPayoutType(value);
        break;
      default:
        break;
    }
  };
  const isPayoutValid = () => {
    if (tempPayoutType === OFFER_PAYOUT_TYPE.SALE) {
      return tempPercentPayout !== null && tempPercentPayout >= 0 && tempPercentPayout <= 100;
    }
    if (tempPayoutType === OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE) {
    return (tempFlatPayout !== null && tempFlatPayout >= 0) &&
             (tempPercentPayout !== null && tempPercentPayout >= 0 && tempPercentPayout <= 100);
    }
    return tempFlatPayout !== null && tempFlatPayout >= 0;
  };

  React.useEffect(() => {
    const validationErrors = getToolTipContent(tempPayoutTitles, tempFlatPayout, tempPercentPayout, payouts, setDisableAddPayout, tempPayoutType, payoutMode);
    const hasInvalidPayout = !isPayoutValid();
    setErrors([...validationErrors, ...(hasInvalidPayout ? ['Payout value must be positive and not greater than 100'] : [])]);
    setDisableAddPayout(validationErrors.length > 0 || hasInvalidPayout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempPayoutTitles, tempFlatPayout, tempPercentPayout, payouts, payoutMode, tempPayoutType]);

  const customPayoutValue = (payoutType) => {
    switch (payoutType) {
      case OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE:
        return (
          <div className="flex-1 ml-4">
            <div className="flex items-center gap-2">
              {/* Flat Payout */}
              <div className="relative w-full max-w-[272px]">
                <span className="absolute text-grey-4 left-3 top-[5px]">$</span>
                <Input
                  type="number"
                  onKeyDown={UseNaturalNumbers}
                  placeholder="0"
                  className="pl-8 w-full max-w-[272px] bg-primary-foreground"
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    setTempFlatPayout(value);
                  }}
                  value={tempFlatPayout ?? ''}
                />
              </div>

              <div className="flex items-center justify-center">+</div>

              {/* Percent Payout */}
              <div className="relative w-full max-w-[272px]">
                <span className="absolute text-grey-4 right-3 top-[5px]">%</span>
                <Input
                  type="number"
                  onKeyDown={UseNaturalNumbers}
                  placeholder="0"
                  className="pr-8 w-full max-w-[272px] bg-primary-foreground"
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    setTempPercentPayout(value);
                  }}
                  value={tempPercentPayout ?? ''}
                />
              </div>
            </div>
          </div>
        );

      case OFFER_PAYOUT_TYPE.CLICK:
      case OFFER_PAYOUT_TYPE.CONVERSION:
        return (
          <div className="relative w-full max-w-[272px]">
            <span className="absolute text-grey-4 left-3 top-[5px]">$</span>
            <Input
              type="number"
              onKeyDown={UseNaturalNumbers}
              placeholder="Payout Value"
              className="pl-8 w-full max-w-[272px] bg-primary-foreground"
              onChange={(e) => {
                const value = e.target.value === '' ? null : Number(e.target.value);
                setTempFlatPayout(value);
              }}
              value={tempFlatPayout ?? ''}
            />
          </div>
        );

      case OFFER_PAYOUT_TYPE.SALE:
        return (
          <div className="relative w-full max-w-[272px]">
            <span className="absolute text-grey-4 right-3 top-[5px]">%</span>
            <Input
              type="number"
              onKeyDown={UseNaturalNumbers}
              placeholder="Payout Value"
              className="pr-8 w-full max-w-[272px] bg-primary-foreground"
              onChange={(e) => {
                const value = e.target.value === '' ? null : Number(e.target.value);
                setTempPercentPayout(value);
              }}
              value={tempPercentPayout ?? ''}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const remove = (index: number) => {
    const currentValues = payouts || [];
    currentValues.splice(index, 1);
    handleFormAsyncActions({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: { key: 'payoutOptions', value: currentValues },
    });
  };

  const handlePayoutRulesChange = (newRules: PayoutRuleInput[]) => {
    setPayoutRules(newRules);
  };

  const handleConditionsChange = (newConditions: PayoutRuleConditionsInput[]) => {
    setConditions(newConditions);
  };

  const handleClickPayoutOption = React.useCallback((key, payoutIndex, payout) => {
    switch (key) {
      case 'delete': {
        if (payout[payoutIndex].isMemberAdded) {
          confirm({
            title: 'Can not remove this payout value',
            icon: <TriangleExclamationIcon style={{ color: 'red' }} />,
            content: source === OFFER_SOURCE.SHOPIFY ? 'This payout option cannot be removed because it has members with active promo codes. Please re-assign all existing members to a different payout option, then re-attempt to delete this payout option.' : 'This Payout option cannot be removed because it has members with active offers. Please re-assign all existing members to a different payout option and then re-attempt deleting this payout option.',
            onOk() {
            },
            cancelButtonProps: {
              style: {
                display: 'none',
              },
            },
            okText: 'Close',
          });
        } else if (payouts.length === 1 && payout[payoutIndex].isDefault) {
          confirm({
            title: 'Offer must have at least one payout option.',
            icon: <ExclamationCircleFilled />,
            content: 'You must create another payout option and make it the default for this offer before removing this payout option.',
            onOk() {
            },
            cancelButtonProps: {
              style: {
                display: 'none',
              },
            },
          });
        } else if (payout[payoutIndex].isDefault && mode === OfferFormModes.Create) {
          let selectedIndex = 0;
          if (payoutIndex === 0) selectedIndex = 1;
          const currentValues = payouts || [];
          currentValues.forEach((element) => {
            element.isDefault = false;
          });
          currentValues[selectedIndex].isDefault = true;
          formRef.setFieldsValue({
            payoutOptions: currentValues,
          });
          handleFormAsyncActions({ action: OfferFormAsyncActions.DEFAULT_PAYOUT, payload: { index: selectedIndex } });
          remove(payoutIndex);
        } else if (payout[payoutIndex] && mode === OfferFormModes.Edit) {
          confirm({
            title: source === OFFER_SOURCE.SHOPIFY ? 'Are you sure you want to remove the default payout option for this offer?' : 'Are you sure you want to remove this default payout?',
            icon: <TriangleExclamationIcon style={{ color: 'red' }} />,
            content: source === OFFER_SOURCE.SHOPIFY ? 'If you remove the default payout option, we will automatically select the most recently created payout option as the new default for this offer. You can always change the default option.' : 'The Payout option you are deleting is currently the default option. A new one will be selected automatically. You can change this by going to offer settings. Do you want to proceed to delete "Standard" payout option?"',
            okText: 'Confirm Remove',
            okButtonProps: {
              style: {
                background: 'white',
                borderColor: 'red',
                color: 'red',
              },
            },
            onOk() {
              const sortedArray: IPayoutVariant[] = sortBy(payouts, (obj) => new Date(obj.createdDate));
              let newIndex = payouts.findIndex((obj) => obj.id === sortedArray[0].id);
              if (newIndex === payoutIndex) {
                newIndex = payouts.findIndex((obj) => obj.id === sortedArray[1].id);
              }
              if (newIndex >= 0) {
                const currentValues = payouts || [];
                currentValues.forEach((element) => {
                  element.isDefault = false;
                });
                currentValues[newIndex].isDefault = true;
                formRef.setFieldsValue({
                  payoutOptions: currentValues,
                });
                handleFormAsyncActions({ action: OfferFormAsyncActions.DEFAULT_PAYOUT, payload: { index: newIndex } });
              }
              remove(payoutIndex);
            },
          });
        } else if (!payout[payoutIndex].isMemberAdded && mode === OfferFormModes.Edit) {
          confirm({
            title: 'Are you sure you want to remove this payout option?',
            icon: <TriangleExclamationIcon style={{ color: 'red' }} />,
            content: source === OFFER_SOURCE.SHOPIFY ? 'You will no longer be able to assign members to this payout option.' : 'This payout option will no longer be available to be assigned to members. ',
            okText: 'Confirm Remove',
            okButtonProps: {
              style: {
                background: 'white',
                borderColor: 'red',
                color: 'red',
              },
            },
            onOk() {
              remove(payoutIndex);
            },
            onCancel() {
            },
            cancelText: 'Cancel',
          });
        } else {
          remove(payoutIndex);
        }
        break;
      }
      case 'default': {
        const currentValues = payouts || [];
        currentValues.forEach((element) => {
          element.isDefault = false;
        });
        currentValues[payoutIndex].isDefault = true;
        formRef.setFieldsValue({
          payoutOptions: currentValues,
        });
        handleFormAsyncActions({ action: OfferFormAsyncActions.DEFAULT_PAYOUT, payload: { index: payoutIndex } });
        break;
      }
      case 'rules': {
        if (payout[payoutIndex].payoutType === OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE || payout[payoutIndex].payoutType === OFFER_PAYOUT_TYPE.CLICK) {
          message.error(`Payout rules are not supported for payout type ${payout[payoutIndex].payoutType === OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE ? 'CPA+CPS' : 'CPC'}. To enable rules, edit your payout type to CPA or CPS.`);
          return;
        }
        setIsPayoutRulesDrawerOpen(true);
        setCurrentPayout(payout[payoutIndex]);
        setCurrentPayoutIndex(payoutIndex);

        // Reset states for new payout
        setPayoutState({
          tempPayoutTitles: payout[payoutIndex].label,
          tempPercentPayout: payout[payoutIndex].percentPayout,
          tempFlatPayout: payout[payoutIndex].flatPayout,
          tempPayoutType: payout[payoutIndex].payoutType
        });

        // Initialize rules and conditions based on current payout only
        const currentPayoutRules = payout[payoutIndex].offerPayoutRules || payout[payoutIndex].payoutRules || [];
        setPayoutRules(currentPayoutRules);

        // Initialize conditions only if there are rules
        if (currentPayoutRules.length > 0) {
          const firstRule = currentPayoutRules[0];
          const newConditions = initializeConditionsFromRule(firstRule);
          setConditions(newConditions);
          handleConditionsChange(newConditions);
        } else {
          // Reset conditions for new payout
          const emptyConditions = [{ type: null, action: PayoutAction.ADD, id: null, operator: PayoutRuleOperator.AND }];
          setConditions(emptyConditions);
          handleConditionsChange(emptyConditions);
        }
        break;
      }
      default: {
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef, handleFormAsyncActions, mode, source, payouts]);

  // Add this helper function
  const initializeConditionsFromRule = (rule) => {
    const newConditions = [];

    if (rule.collections?.length > 0) {
      newConditions.push({
        type: 'Collections',
        collections: rule.collections,
        action: PayoutAction.ADD,
        id: null
      });
    }

    if (rule.duration) {
      newConditions.push({
        type: 'Date Range',
        duration: {
          startDate: moment(rule.duration.startDate),
          endDate: moment(rule.duration.endDate)
        },
        action: PayoutAction.ADD,
        id: null
      });
    }

    if (rule.orderSubtotal?.value) {
      newConditions.push({
        type: 'Order Subtotal',
        orderSubtotal: rule.orderSubtotal,
        action: PayoutAction.ADD,
        id: null
      });
    }

    if (Array.isArray(rule.products) && rule.products.length > 0) {
      newConditions.push({
        type: 'Product',
        products: rule.products,
        action: PayoutAction.ADD,
        id: null
      });
    }

    if (rule.recurringLimit) {
      let limit;
      if (rule.recurringLimit.isLimitPayoutToFirstOrder) {
        limit = 1;
      } else if (rule.recurringLimit.isPayoutAppliedToAllRecurringOrders) {
        limit = 0;
      } else if (rule.recurringLimit.limitPayoutToMultipleRecurringOrder) {
        limit = rule.recurringLimit.limitPayoutToMultipleRecurringOrder;
      }

      if (limit !== undefined) {
        newConditions.push({
          type: ConditionType.RECURRING_ORDER,
          recurringLimit: {
            isLimitPayoutToFirstOrder: false,
            isPayoutAppliedToAllRecurringOrders: true,
            limitPayoutToMultipleRecurringOrder: null,
          },
          action: PayoutAction.ADD,
          id: null
        });
      }
    }

    return newConditions.length > 0 ? newConditions : [{ type: null, action: PayoutAction.ADD, id: null }];
  };

  const addPayout = () => {
    if (payouts.length < MAX_PAYOUT_OPTIONS) {
      setIsAddingNew(true);
      setPayoutMode(PAYOUT_MODE.CREATE);
      setTempPayoutTitles('');
      setTempPercentPayout(null);
      setTempFlatPayout(null);
      setTempPayoutType(OFFER_PAYOUT_TYPE.SALE);
    } else {
      message.error(`You have reached the maximum limit of ${MAX_PAYOUT_OPTIONS} payout options for this offer`);
    }
  };

  const handleClickPayoutType = (value: OFFER_PAYOUT_TYPE) => {
    setTempPayoutType(value);
    setTempFlatPayout(tempPercentPayout);
  };

  const handleAddPayout = () => {
    setIsAddingNew(false);
    const newPayout: IPayoutVariant = {
      label: tempPayoutTitles,
      isDefault: payouts.length === 0,
      payoutType: tempPayoutType,
      isMemberAdded: false,
      percentPayout: tempPercentPayout ?? 0,
      flatPayout: tempFlatPayout ?? 0,
    };
    const tempPayout: IPayoutVariant[] = [...payouts, newPayout];
    handleFormAsyncActions({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: { key: 'payoutOptions', value: tempPayout },
    });
    handleClear();
  };

  const handleEdit = (index: number) => {
    const selectedPayout = payouts[index];
    // Check both offerPayoutRules and payoutRules
    const payoutRulesToUse = (selectedPayout as any).offerPayoutRules || selectedPayout.payoutRules;
    const hasMultipleRules = !!payoutRulesToUse?.length;

    if (hasMultipleRules) {
      // Open PayoutRulesDrawer if payout has rules
      setIsPayoutRulesDrawerOpen(true);
      setCurrentPayout(selectedPayout);
      setCurrentPayoutIndex(index);
      setPayoutState({
        tempPayoutTitles: selectedPayout.label,
        tempPercentPayout: selectedPayout.percentPayout,
        tempFlatPayout: selectedPayout.flatPayout,
        tempPayoutType: selectedPayout.payoutType as OFFER_PAYOUT_TYPE
      });
      setTempPayoutTitles(selectedPayout.label);
      setTempPercentPayout(selectedPayout.percentPayout);
      setTempFlatPayout(selectedPayout.flatPayout);
      setTempPayoutType(selectedPayout.payoutType as OFFER_PAYOUT_TYPE);

      // Initialize payout rules if they exist
      if (payoutRulesToUse) {
        setPayoutRules(payoutRulesToUse);
        if (payoutRulesToUse.length > 0) {
          initializeConditionsFromRule(payoutRulesToUse[0]);
        }
      }
    } else {
      // Use inline editing for simple payouts
      setPayoutMode(PAYOUT_MODE.EDIT);
      setEditingIndex(index);
      setTempPayoutTitles(selectedPayout.label);
      setTempPayoutType(selectedPayout.payoutType as OFFER_PAYOUT_TYPE);
      setTempPercentPayout(selectedPayout.percentPayout);
      setTempFlatPayout(selectedPayout.flatPayout);
    }
  };

  const handleEditPayout = (index: number) => {
    setEditingIndex(null);
    const existingPayout = payouts[index];
    const updatedPayout: IPayoutVariant = {
      label: isPayoutRulesDrawerOpen ? payoutState.tempPayoutTitles : tempPayoutTitles,
      isDefault: existingPayout.isDefault,
      payoutType: isPayoutRulesDrawerOpen ? payoutState.tempPayoutType : tempPayoutType,
      isMemberAdded: existingPayout.isMemberAdded,
      percentPayout: isPayoutRulesDrawerOpen ? payoutState.tempPercentPayout : tempPercentPayout,
      flatPayout: isPayoutRulesDrawerOpen ? payoutState.tempFlatPayout : tempFlatPayout,
      id: existingPayout.id,
    };

    if (isPayoutRulesDrawerOpen && payoutRules.length > 0) {
      // Check if any values in the rules have changed
      const hasRuleChanges = payoutRules.some((rule, idx) => {
        const existingRule = existingPayout.payoutRules?.[idx];
        if (!existingRule) return true; // New rule added

        return (
          rule.flatPayout !== existingRule.flatPayout ||
          rule.percentPayout !== existingRule.percentPayout ||
          JSON.stringify(Array.isArray(rule.collections) ? rule.collections : []) !== JSON.stringify(Array.isArray(existingRule.collections) ? existingRule.collections : []) ||
          JSON.stringify(Array.isArray(rule.products) ? rule.products : []) !== JSON.stringify(Array.isArray(existingRule.products) ? existingRule.products : []) ||
          JSON.stringify(rule.orderSubtotal) !== JSON.stringify(existingRule.orderSubtotal) ||
          JSON.stringify(rule.duration) !== JSON.stringify(existingRule.duration) ||
          JSON.stringify(rule.recurringLimit) !== JSON.stringify(existingRule.recurringLimit)
        );
      });
      updatedPayout.payoutRules = payoutRules.map((rule) => ({
        id: rule.id,
        // If rule has its own action (ADD/REMOVE) use that, otherwise set based on changes
        action: rule.action === PayoutAction.ADD || rule.action === PayoutAction.REMOVE ? rule.action : (hasRuleChanges ? PayoutAction.UPDATE : PayoutAction.NOUPDATE),
        collections: Array.isArray(rule.collections) ? rule.collections.map((collection) => ({
          id: collection.id,
          name: collection.name,
          advertiserId: collection.advertiserId || null
        })) : [],
        duration: rule.duration,
        flatPayout: rule.flatPayout,
        orderSubtotal: rule.orderSubtotal || { value: 0, operator: null },
        percentPayout: rule.percentPayout,
        products: Array.isArray(rule.products) ? rule.products.map((product) => ({
          id: String(product.id),
          name: product.name,
          type: product.type || PRODUCT_CONDITION_TYPE.PRODUCT_ID,
          imageUrl: product.imageUrl || null,
          advertiserId: product.advertiserId || null
        })) : null,
        recurringLimit: rule.recurringLimit ? {
          isLimitPayoutToFirstOrder: rule.recurringLimit.isLimitPayoutToFirstOrder,
          isPayoutAppliedToAllRecurringOrders: rule.recurringLimit.isPayoutAppliedToAllRecurringOrders,
          limitPayoutToMultipleRecurringOrder: rule.recurringLimit.limitPayoutToMultipleRecurringOrder
        } : null,
        operator: rule.operator as PayoutRuleOperator || PayoutRuleOperator.AND
      }));
    }
    const tempPayout: IPayoutVariant[] = [...payouts];
    tempPayout[index] = updatedPayout;
    handleFormAsyncActions({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: { key: 'payoutOptions', value: tempPayout },
    });
    handleClear();
  };

  const handleCancelPayout = () => {
    setIsAddingNew(false);
    setEditingIndex(null);
    handleClear();
  };

  const handleClear = () => {
    setTempPayoutTitles('');
    setTempPercentPayout(null);
    setTempFlatPayout(null);
    setTempPayoutType(OFFER_PAYOUT_TYPE.CONVERSION);
  };

  const togglePayoutExpansion = (payoutId: number) => {
    setExpandedPayouts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(payoutId)) {
        newSet.delete(payoutId);
      } else {
        newSet.add(payoutId);
      }
      return newSet;
    });
  };

  return (
    <div>
      {payouts.map((payout, index) => {
        const hasMultipleCommissions = ((payout as any).offerPayoutRules?.length > 0 || payout.payoutRules?.length > 0);
        return (
          <Card
            key={payout.id}
            className={`flex flex-col mb-2 rounded-xl transition-all duration-300 ease-in-out ${
            editingIndex === index ? 'bg-grey-1' : ''
          }`}
          >
            {editingIndex !== index ? (
              hasMultipleCommissions ? (
                <button
                  onClick={() => togglePayoutExpansion(payout.id)}
                  type="button"
                  className="w-full text-left hover:bg-grey-1 transition-all duration-300 ease-in-out"
                >
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-x-4 w-[85%]">
                      <div className="font-medium w-[272px]">
                        {payout.label}
                        {payout.isDefault && <Tag className="ml-2" bgColor="bg-teal-4" textColor="text-grey-0" label="Default" />}
                      </div>
                      <div className="flex items-center justify-between flex-1">
                        <div className="flex items-center gap-x-4">
                          <div className="font-medium">
                            {((payout as any).offerPayoutRules?.length > 0 || payout.payoutRules?.length > 0) ? (
                              <div className="text-[#5d6974]">
                                <div className="flex items-center h-6">
                                  <span className="font-medium text-[#16282d] text-sm leading-6">Multiple</span>
                                  <span className="text-[#5d6974] text-sm leading-5">&nbsp;Commissions</span>
                                </div>
                              </div>
                            ) : (
                              getPayoutText(payout.payoutType, payout.flatPayout, payout.percentPayout)
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-[10%]">
                        <Tag
                          bgColor={getUniqueColorForPayout(payout.payoutType)}
                          label={getPayoutTagInitials(payout.payoutType)}
                          textColor="text-primary"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <Button
                        variant="outline"
                        size="smallIcon"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(index);
                        }}
                        className="p-2 cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5 border-0"
                      >
                        <EditOutlined />
                      </Button>
                      <Select
                        value={payout.payoutType}
                        onValueChange={(value) => {
                          handleClickPayoutOption(value, index, payouts);
                        }}
                      >
                        <SelectTrigger
                          className="w-max-content border-0 font-normal text-primary p-2 h-6 focus:outline-none focus:ring-0 focus:border-none transition-all duration-300 ease-in-out"
                          showIcon={false}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EllipsisIcon className="w-3 h-3 text-grey-6" />
                        </SelectTrigger>
                        <SelectContent>
                          {tieredCommissions && (source === OFFER_SOURCE.SHOPIFY || source === OFFER_SOURCE.PROMO_LINK ||
                          (source === OFFER_SOURCE.TUNE &&
                           conversionType === OFFER_CONVERSION_TYPE.CONVERSIONS &&
                           (affiliateLinkData.conversionTrackingType === OFFER_TRACKING_TYPE.JAVASCRIPT_POSTBACK ||
                            affiliateLinkData.conversionTrackingType === OFFER_TRACKING_TYPE.SERVER_POSTBACK_TRANSACTION_ID))) && (
                              <SelectItem value="rules">
                                Payout Rules
                              </SelectItem>
                            )}
                          {!payout.isDefault && (
                            <SelectItem value="default">
                              Make Default
                            </SelectItem>
                          )}
                          <SelectItem value="delete" className="text-red-500">
                            Delete
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {hasMultipleCommissions &&
                  expandedPayouts.has(payout.id) && (
                    <div className="flex flex-col bg-grey-2 border-t border-grey-2 p-2 gap-2 transition-all duration-300 ease-in-out animate-in slide-in-from-top-2 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top-2">
                      {/* Existing Rules */}
                      {((payout as any).offerPayoutRules || payout.payoutRules)
                        ?.filter((rule) => rule.action !== PayoutAction.REMOVE)
                        .map((rule, ruleIndex) => (
                          <div key={ruleIndex}>
                            <div className="flex items-start px-3 py-2 bg-white rounded-xl">
                              <div className="w-[40%]">
                                <div className="font-medium text-[#16282d]">
                                  {getPayoutText(payout.payoutType, rule.flatPayout, rule.percentPayout)}
                                </div>
                              </div>
                              <div className="w-[60%]">
                                <PayoutTags
                                  tags={[
                                    ...(Array.isArray(rule.collections)
                                      ? rule.collections.map((collection) => ({
                                          id: collection.id,
                                          name: collection.name,
                                          type: 'collection' as const
                                        }))
                                      : []),
                                    ...(Array.isArray(rule.products)
                                      ? rule.products.map((product) => ({
                                          id: String(product.id),
                                          name: product.name,
                                          type: 'product' as const,
                                          imageUrl: product.imageUrl
                                        }))
                                      : []),
                                    ...(rule.orderSubtotal?.value > 0 ? [{
                                      id: 'orderSubtotal',
                                      name: `Order Subtotal ${OPERATOR_DISPLAY[rule.orderSubtotal.operator]} $${rule.orderSubtotal.value}`,
                                      type: 'order_subtotal' as const
                                    }] : []),
                                    ...(rule.duration ? [{
                                      id: 'dateRange',
                                      name: `${moment(rule.duration.startDate).format('MMM D')} - ${moment(rule.duration.endDate).format('MMM D, YYYY')}`,
                                      type: 'date_range' as const
                                    }] : []),
                                    ...(rule.recurringLimit ? [{
                                      id: 'recurring',
                                      name: ConditionType.RECURRING_ORDER,
                                      type: 'recurring' as const,
                                      recurringLimit: rule.recurringLimit.limit ? {
                                        isLimitPayoutToFirstOrder: rule.recurringLimit.limit === 1,
                                        isPayoutAppliedToAllRecurringOrders: rule.recurringLimit.limit === 0,
                                        limitPayoutToMultipleRecurringOrder: rule.recurringLimit.limit > 1 ? rule.recurringLimit.limit : null
                                      } : rule.recurringLimit
                                    }] : [])
                                  ]}
                                  showDivider
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      {/* Parent/Default Commission Rule */}
                      <div className="flex items-start px-3 py-2 bg-white rounded-xl">
                        <div className="w-[40%]">
                          <div className="font-medium text-[#16282d]">
                            {getPayoutText(payout.payoutType, payout.flatPayout, payout.percentPayout)}
                          </div>
                        </div>
                        <div className="w-[60%] flex items-center gap-x-2">
                          <span className="text-sm text-grey-5 mr-1 whitespace-nowrap">On:</span>
                          <Tag
                            icon={<LockIcon className="w-3.5 h-3.5" />}
                            label="Everything Else"
                            bgColor="bg-yellow-6"
                            textColor="text-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </button>
              ) : (
                <div className="w-full text-left transition-all duration-300 ease-in-out">
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-x-4 w-[85%]">
                      <div className="font-medium w-[272px]">
                        {payout.label}
                        {payout.isDefault && <Tag className="ml-2" bgColor="bg-teal-4" textColor="text-grey-0" label="Default" />}
                      </div>
                      <div className="flex items-center justify-between flex-1">
                        <div className="flex items-center gap-x-4">
                          <div className="font-medium">
                            {((payout as any).offerPayoutRules?.length > 0 || payout.payoutRules?.length > 0) ? (
                              <div className="text-[#5d6974]">
                                <div className="flex items-center h-6">
                                  <span className="font-medium text-[#16282d] text-sm leading-6">Multiple</span>
                                  <span className="text-[#5d6974] text-sm leading-5">&nbsp;Commissions</span>
                                </div>
                              </div>
                            ) : (
                              getPayoutText(payout.payoutType, payout.flatPayout, payout.percentPayout)
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-[10%]">
                        <Tag
                          bgColor={getUniqueColorForPayout(payout.payoutType)}
                          label={getPayoutTagInitials(payout.payoutType)}
                          textColor="text-primary"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <Button
                        variant="outline"
                        size="smallIcon"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(index);
                        }}
                        className="p-2 cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5 border-0"
                      >
                        <EditOutlined />
                      </Button>
                      <Select
                        value={payout.payoutType}
                        onValueChange={(value) => {
                          handleClickPayoutOption(value, index, payouts);
                        }}
                      >
                        <SelectTrigger
                          className="w-max-content border-0 font-normal text-primary p-2 h-6 focus:outline-none focus:ring-0 focus:border-none transition-all duration-300 ease-in-out"
                          showIcon={false}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EllipsisIcon className="w-3 h-3 text-grey-6" />
                        </SelectTrigger>
                        <SelectContent>
                          {tieredCommissions && (source === OFFER_SOURCE.SHOPIFY || source === OFFER_SOURCE.PROMO_LINK ||
                          (source === OFFER_SOURCE.TUNE &&
                           conversionType === OFFER_CONVERSION_TYPE.CONVERSIONS &&
                           (affiliateLinkData.conversionTrackingType === OFFER_TRACKING_TYPE.JAVASCRIPT_POSTBACK ||
                            affiliateLinkData.conversionTrackingType === OFFER_TRACKING_TYPE.SERVER_POSTBACK_TRANSACTION_ID))) && (
                              <SelectItem value="rules">
                                Payout Rules
                              </SelectItem>
                            )}
                          {!payout.isDefault && (
                            <SelectItem value="default">
                              Make Default
                            </SelectItem>
                          )}
                          <SelectItem value="delete" className="text-red-500">
                            Delete
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex items-center w-full gap-x-6 transition-all duration-300 ease-in-out px-3 py-1">
                <div className="flex items-center w-[90%] gap-x-4">
                  <Input type="text" maxLength={60} placeholder="Payout Title" className="w-full max-w-[272px] bg-primary-foreground transition-all duration-300 ease-in-out" value={tempPayoutTitles} onChange={(e) => setTempPayoutTitles(e.target.value)} />
                  {customPayoutValue(tempPayoutType)}
                  <Select value={tempPayoutType} onValueChange={handleClickPayoutType}>
                    <SelectTrigger className={`w-max border-0 font-normal text-primary focus:outline-none focus:ring-0 ${getUniqueColorForPayout(tempPayoutType)} h-6 px-2 transition-all duration-300 ease-in-out`}>
                      <SelectValue placeholder="Select Payout Type">
                        {getPayoutTagInitials(tempPayoutType)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={OFFER_PAYOUT_TYPE.CONVERSION}>
                        {getPayoutTag(OFFER_PAYOUT_TYPE.CONVERSION, formattedConversionType)}
                      </SelectItem>
                      <SelectItem value={OFFER_PAYOUT_TYPE.SALE}>
                        {getPayoutTag(OFFER_PAYOUT_TYPE.SALE, formattedConversionType)}
                      </SelectItem>
                      <SelectItem value={OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE}>
                        {getPayoutTag(OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE, formattedConversionType)}
                      </SelectItem>
                      {showCostPerClick && (
                        <SelectItem value={OFFER_PAYOUT_TYPE.CLICK}>
                          {getPayoutTag(OFFER_PAYOUT_TYPE.CLICK, formattedConversionType)}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center w-[10%] justify-end gap-x-2">
                  <Button variant="outline" size="smallIcon" type="button" onClick={handleCancelPayout} className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5">
                    <XmarkIcon />
                  </Button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="smallIcon"
                        type="button"
                        onClick={() => handleEditPayout(index)}
                        disabled={disableAddPayout}
                        className={`cursor-pointer transition-all duration-300 ease-in-out ${disableAddPayout ? '!pointer-events-auto opacity-50' : ''}`}
                      >
                        <CheckIcon />
                      </Button>
                    </TooltipTrigger>
                    {errors.length > 0 && (
                      <TooltipContent>
                        <span>
                          <ul className="text-primary-foreground">
                            {errors.map((error, index) => (
                              <li key={index}>{error} </li>
                            ))}
                          </ul>
                        </span>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {isAddingNew && (
        <Card
          className="flex items-center justify-between px-3 py-2 bg-grey-1 border border-grey-2 gap-x-6 h-10 rounded-xl mb-2 transition-all duration-300 ease-in-out"
        >
          <div className="flex items-center w-full gap-x-6">
            <div className="flex items-center w-[90%] gap-x-4">
              <Input type="text" maxLength={60} placeholder="Payout Title" className="w-full max-w-[272px] bg-primary-foreground transition-all duration-300 ease-in-out" value={tempPayoutTitles} onChange={(e) => setTempPayoutTitles(e.target.value)} />
              {customPayoutValue(tempPayoutType)}
              <Select value={tempPayoutType} onValueChange={handleClickPayoutType}>
                <SelectTrigger className={`w-max border-0 font-normal text-primary focus:outline-none focus:ring-0 ${getUniqueColorForPayout(tempPayoutType)} h-6 px-2 transition-all duration-300 ease-in-out`}>
                  <SelectValue placeholder="Select Payout Type">
                    {getPayoutTagInitials(tempPayoutType)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OFFER_PAYOUT_TYPE.CONVERSION}>
                    {getPayoutTag(OFFER_PAYOUT_TYPE.CONVERSION, formattedConversionType)}
                  </SelectItem>
                  <SelectItem value={OFFER_PAYOUT_TYPE.SALE}>
                    {getPayoutTag(OFFER_PAYOUT_TYPE.SALE, formattedConversionType)}
                  </SelectItem>
                  <SelectItem value={OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE}>
                    {getPayoutTag(OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE, formattedConversionType)}
                  </SelectItem>
                  {showCostPerClick && (
                    <SelectItem value={OFFER_PAYOUT_TYPE.CLICK}>
                      {getPayoutTag(OFFER_PAYOUT_TYPE.CLICK, formattedConversionType)}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center w-[10%] justify-end gap-x-2">
              <Button variant="outline" size="smallIcon" type="button" onClick={handleCancelPayout} className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5">
                <XmarkIcon />
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="smallIcon"
                    type="button"
                    onClick={handleAddPayout}
                    disabled={disableAddPayout}
                    className={`cursor-pointer transition-all duration-300 ease-in-out ${disableAddPayout ? '!pointer-events-auto opacity-50' : ''}`}
                  >
                    <CheckIcon />
                  </Button>
                </TooltipTrigger>
                {errors.length > 0 && (
                  <TooltipContent>
                    <span>
                      <ul className="text-primary-foreground">
                        {errors.map((error, index) => (
                          <li key={index}>{error} </li>
                        ))}
                      </ul>
                    </span>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>
        </Card>
      )}
      <Button
        variant="ghost"
        className={`flex items-center mb-2 gap-2 p-0 w-max h-auto ${(isAddingNew || editingIndex !== null) ? 'opacity-50' : ''}`}
        onClick={addPayout}
        disabled={isAddingNew || editingIndex !== null}
      >
        <PlusIcon />
        <P className="text-sm font-medium text-primary underline" fontWeight="font-medium">
          Add Payout
        </P>
      </Button>
      {isPayoutRulesDrawerOpen && (<PayoutRulesDrawer
        open={isPayoutRulesDrawerOpen}
        onOpenChange={setIsPayoutRulesDrawerOpen}
        payout={currentPayout}
        formattedConversionType={formattedConversionType}
        payoutState={payoutState}
        updatePayoutState={updatePayoutState}
        handleEditPayout={handleEditPayout}
        payoutIndex={currentPayoutIndex}
        payoutRules={payoutRules}
        conditions={conditions}
        onPayoutRulesChange={handlePayoutRulesChange}
        onConditionsChange={handleConditionsChange}
        clientsForSync={shopifyData?.clientsForSync}
      />)}
    </div>
  );
});
RefreshOfferPayoutOptions.displayName = 'RefreshOfferPayoutOptions';
